import { type Ref } from 'react';

/* eslint-disable-next-line no-restricted-imports */
import RootPopper from '@mui/material/Popper';

import type * as types from './types';

import { mergeSx } from '../../util/mui/sx';
import { quantitativeSizeToString } from '../../util/sizes';

import { useModifiers, type UseModifiersParams } from './hooks';

export interface PopperProps
  extends
    Omit<types.MuiPopperProps, 'modifiers' | 'open'>,
    Partial<types.CustomModifierApplicableProps>,
    Pick<UseModifiersParams, '__privateUseModifiersParentScope__' | 'modifiers'> {
  readonly isOpen: boolean;
  readonly ref?: Ref<HTMLDivElement>;
}

/**
 * The internalized form of Material UI's {@link RootPopper} component.
 */
export const Popper = ({
  __privateUseModifiersParentScope__,
  children,
  isOpen,
  maxHeight,
  modifiers: _modifiers,
  offset,
  ref,
  ...props
}: PopperProps) => {
  const { hasCustomModifier, modifiers } = useModifiers({
    __privateUseModifiersParentScope__,
    maxHeight: maxHeight ?? null,
    modifiers: _modifiers,
    offset: offset ?? null,
    placement: props.placement ?? null,
  });
  return (
    <RootPopper
      {...props}
      modifiers={modifiers}
      open={isOpen}
      ref={ref}
      sx={mergeSx(props.sx, {
        '& .MuiPaper-root': {
          maxHeight:
            maxHeight === undefined || hasCustomModifier('ConstrainMaxHeightToViewport')
              ? undefined
              : quantitativeSizeToString(maxHeight, 'px'),
          overflow: 'auto',
          position: 'relative',
        },
      })}
    >
      {children}
    </RootPopper>
  );
};
